import {useLocalStorage} from '@vueuse/core'
import {useCanSwitchUi} from '@/composables/useCanSwitchUi'
import {
  computed,
  hasInjectionContext,
  inject,
  provide,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type WritableComputedRef,
} from 'vue'

export const NEW_UI_LOCAL_STORAGE_KEY = 'new_ui_rollout'

export type UiVersion = 'legacy' | 'new'

const UI_VERSION_KEY: InjectionKey<ComputedRef<UiVersion | undefined>> = Symbol('uiVersion')

export function provideUiVersion(uiVersion: MaybeRefOrGetter<UiVersion | undefined>) {
  const providedUiVersion = computed(() => toValue(uiVersion))
  provide(UI_VERSION_KEY, providedUiVersion)
  return providedUiVersion
}

export function useUiVersion(localUiVersion?: MaybeRefOrGetter<UiVersion | undefined>): WritableComputedRef<UiVersion> {
  const canSwitchUi = useCanSwitchUi()
  const uiPreference = useLocalStorage(NEW_UI_LOCAL_STORAGE_KEY, true)
  const inheritedUiVersion = hasInjectionContext() ? inject(UI_VERSION_KEY, undefined) : undefined
  const scopedUiVersion = computed(() => {
    const version = localUiVersion === undefined ? inheritedUiVersion?.value : toValue(localUiVersion)
    return version
  })

  return computed({
    get: () => {
      if (scopedUiVersion.value) {
        return scopedUiVersion.value
      }
      return canSwitchUi.value && !uiPreference.value ? 'legacy' : 'new'
    },
    set: (version) => {
      if (!scopedUiVersion.value && canSwitchUi.value) {
        uiPreference.value = version === 'new'
      }
    },
  })
}

export function useNewUiEnabled(localUiVersion?: MaybeRefOrGetter<UiVersion | undefined>) {
  const uiVersion = useUiVersion(localUiVersion)

  return computed({
    get: () => uiVersion.value === 'new',
    set: (enabled) => {
      uiVersion.value = enabled ? 'new' : 'legacy'
    },
  })
}
